import { Injectable } from '@angular/core';
import { ControlService } from './control.service';
import { DeviceSettingsService } from './device-settings.service';
import { PlayerService } from './player.service';
import { AppConstants } from 'src/app/app-constants';
import { CallStoreService } from './call-store/call-store.service';
import { MediaStreamService } from './media-stream/media-stream.service';
import { CameraStreamMonitorService } from 'src/app/services/learner/camera-stream-monitor.service';

@Injectable({
  providedIn: 'root'
})
export class UserVideoService {
  controlService: ControlService | undefined;
  deviceSettingsService: DeviceSettingsService | undefined;
  playerService: PlayerService | undefined;
  public userVideoStream: MediaStream | undefined;
  userAudioStream: MediaStream | undefined;
  video: HTMLVideoElement | undefined;
  videoAccessDenied: boolean = false;

  public isUserSpeaking: boolean = false;

  // audio levels
  audioContext: AudioContext;
  analyser: AnalyserNode;
  microphone: MediaStreamAudioSourceNode | undefined;
  animationFrame: number | undefined;

  constructor(
    private callStore: CallStoreService,
    private mediaStreamService: MediaStreamService,
    private cameraMonitorService: CameraStreamMonitorService
  ) {
    this.audioContext = new AudioContext();
    this.analyser = this.audioContext.createAnalyser();
  }

  setControlService(controlService: ControlService): void {
    this.controlService = controlService;
  }

  setDeviceSettingsService(deviceSettingsService: DeviceSettingsService): void {
    this.deviceSettingsService = deviceSettingsService;
  }

  setPlayerService(playerService: PlayerService): void {
    this.playerService = playerService;
  }

  displayUserVideo(): void {
    const video = document.getElementById('user-video') as HTMLVideoElement;
    if (!video) return;

    this.video = video;
    // only display the user video if the user has a camera selected from the device settings
    const defaultDevice = this.videoAccessDenied ? '' : 'default';
    const videoSelected = this.deviceSettingsService?.getVideoDeviceLocalStorage() || defaultDevice;
    if (this.video && videoSelected.length > 0) {
      const idealWidth = this.video.offsetWidth;
      const device = this.deviceSettingsService?.getVideoDeviceLocalStorage();
      const deviceSaved = device || '';
      // constraints for the video saved by the user
      const cDevice = {
        video: {
          width: { ideal: idealWidth },
          deviceId: { exact: deviceSaved },
        },
        audio: false,
      };
      // constraints for the video not saved by the user
      const cNoDevice: MediaStreamConstraints = {
        video: {
          width: { ideal: idealWidth },
        },
        audio: false,
      };
      const constraints = deviceSaved ? cDevice : cNoDevice;

      this.mediaStreamService.createVideoStream(
        constraints
      ).then((stream) => {
        this.userVideoStream = stream;
        // Start camera monitoring if this is a CERTIFICATION + GUIDED call
        const practiceType = this.callStore.getModuleAssignmentType();
        const callType = this.callStore.getCallType();
        if (practiceType === AppConstants.TYPE_CERTIFICATION && callType === AppConstants.CALL_TYPE_GUIDED) {
          this.cameraMonitorService.startMonitoring(stream);
        }
        this.handleVideoStream(stream);
      }).catch((error) => {
        this.handleVideoError(error);
      });
    } else {
      const currentView = this.callStore.getCurrentView();
      const isPracticeView = AppConstants.VIEW_PRACTICE === currentView;
      const isNotPaused = !this.callStore.getIsPaused();

      if (isPracticeView && isNotPaused) {
        setTimeout(() => {
          this.displayUserVideo();
        }, 500);
      }
    }
  }

  private handleVideoStream(stream: MediaStream): void {
    if (this.deviceSettingsService) {
      this.deviceSettingsService.isCameraUnavailable = false;
    }
    if (this.video) {
      // if the call is not paused, display the user video
      const isPaused = this.callStore.getIsPaused();
      const isVideoOn = this.controlService?.isVideoOn;
      if (!isPaused && isVideoOn) {
        try {
          this.video.srcObject = stream;
          this.video.addEventListener('canplay', function onCanPlay() {
            this.removeEventListener('canplay', onCanPlay);
            this.play().catch((error) => {
              console.error('Error playing the user video:', error);
            });
          });
        } catch (error) {
          this.mediaStreamService.stopVideoStream();
          this.userVideoStream = undefined;
          throw new Error(
            'Error playing the user video on user video service'
          );
        }
      }
    }
  }

  private handleVideoError(error: any): void {
    if (error.name === 'OverconstrainedError' && this.deviceSettingsService) {
      this.deviceSettingsService.videoDeviceSelected = '';
      this.deviceSettingsService.removeVideoDeviceLocalStorage();
      this.displayUserVideo();
    } else {
      this.videoAccessDenied = true;
    }
  }

  stopUserVideo(): void {
    this.userVideoStream = undefined;
    if (this.video) {
      this.video.srcObject = null;
    }
  }

}
